import { FC, ReactNode } from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import {
  Button,
  Colors,
  FInter,
  FlexBox,
  useToasts,
} from '@brg/ui';
import { AppNodeMode, MiniApplicationManifest } from 'core/helpers/dna';
import { PageViewportLayout } from '../../components/template';
import { whiteTheme } from '../../themes/whiteTheme';
import type { ApplicationThemeT } from '../../themes';
import { ApplicationTheme } from '../../themes';
import { isLowMode } from '../../helpers/lowmode';
import { customThemeNameOrUndefined } from '../../helpers/customTheme';

const manifest: MiniApplicationManifest = {
  // Название мини-приложения - и document.title
  title: 'Console',

  // объект редукторов (redux)
  reducer: {},

  theme: whiteTheme as ApplicationThemeT,

  // авторизация
  auth: {
    productionClientId: '',
    developClientId: '',
  },
};

const ConsoleSectionComponentStyle = {
  border: `1px solid ${Colors.galBlack31}`,
  padding: '16px',
  borderRadius: '8px',
  minHeight: '180px',
};

export interface ConsoleSectionComponentProps {
  title: string;
  description: ReactNode;
  children?: ReactNode;
}
export const ConsoleSectionComponent: FC<ConsoleSectionComponentProps> = ({
  title,
  description,
  children,
}) => (
  <FlexBox
    flex="1"
    direction="column"
    style={ConsoleSectionComponentStyle}
  >
    <FInter.TextFlex>{title}</FInter.TextFlex>
    <FInter.SmallTextDiv color={Colors.galBlack47}>{description}</FInter.SmallTextDiv>
    {children}
  </FlexBox>
);

/**
 * Компонент для примера работы маршрутизатора
 */
const Example: FC = () => {
  const { info } = useToasts();
  const useLow = isLowMode();
  const customTheme = customThemeNameOrUndefined();

  return (
    <FlexBox color={Colors.geoWhite100}>
      <PageViewportLayout>
        <FInter.MainTitleSmallDiv color={Colors.galBlack100}>Консоль</FInter.MainTitleSmallDiv>

        <FInter.SmallTextDiv color={Colors.galBlack73}>
          Панель разработки и управления
        </FInter.SmallTextDiv>

        <FlexBox gx={16} offsetTop={16}>
          <ConsoleSectionComponent
            title="Тема"
            description={(
              <div>
                Выбор темы повлияет на отображение проекта. Тема выбирается из
                <b> ApplicationTheme </b>
                и устанавливается в
                <b> ThemeProvider</b>
              </div>
            )}
          >
            <FlexBox offsetTop="auto" gx={8}>
              {Object.keys(ApplicationTheme).map((value, index) => value === customTheme ? null : (
                  <Button.GreenSmall
                    onClick={() => {
                      localStorage.setItem('custom-theme', value);
                      info(`установлена тема: ${value}`);
                    }}
                    key={index}
                    color={Colors.galBlack100}
                  >
                    {value}
                  </Button.GreenSmall>
                ),
              )}
              <Button.YellowSmall
                onClick={() => {
                  localStorage.removeItem('custom-theme');
                }}
              >
                remove data
              </Button.YellowSmall>
            </FlexBox>
          </ConsoleSectionComponent>
          <ConsoleSectionComponent
            title="Низкое разрешение"
            description={<div>Картинки будут принудительно загружаться в низком разрешении</div>}
          >
            <FlexBox offsetTop="auto" gx={8} align="center">
              {!useLow && (
                <Button.GreenSmall
                  onClick={() => {
                    localStorage.setItem('low-mode', 'yes');
                  }}
                >
                  включить low image mode
                </Button.GreenSmall>
              )}
              {useLow && (
                <Button.GreenSmall
                  onClick={() => {
                    localStorage.removeItem('low-mode');
                  }}
                >
                  отключить low image mode
                </Button.GreenSmall>
              )}
            </FlexBox>
          </ConsoleSectionComponent>
        </FlexBox>
        <FlexBox gx={16} offsetTop={16}>
          <ConsoleSectionComponent
            title="Очистка хранилища"
            description={(
              <div>
                Выполняет полную очистку хранилища веб-приложения (я надеюсь, вы понимаете,
                что делает эта кнопка)
              </div>
            )}
          >
            <FlexBox offsetTop={8} gy={8} offsetBottom={8} direction={'column'}>
              <FInter.SmallTextDiv>
                Local Storage: {localStorage.length} items
              </FInter.SmallTextDiv>
              <FInter.SmallTextDiv>
                Session Storage: {sessionStorage.length} items
              </FInter.SmallTextDiv>
              <FInter.SmallTextDiv>
                Cookies: {document.cookie.length} bytes
              </FInter.SmallTextDiv>
            </FlexBox>
            <FlexBox offsetTop="auto" gx={8}>
              <Button.GreenSmall
                onClick={() => {
                  localStorage.clear();
                  document.cookie = '';
                  sessionStorage.clear();
                  info('Выполнена очистка: localStorage, sessionStorage, cookies');
                }}
              >
                Очистить хранилище веб-приложения
              </Button.GreenSmall>
            </FlexBox>
          </ConsoleSectionComponent>
        </FlexBox>
        <FlexBox offsetTop={16}>
          <Button.YellowMedium onClick={() => window.location.href = '/'} style={{ width: '100%' }}>
            Закрыть консоль
          </Button.YellowMedium>
        </FlexBox>
      </PageViewportLayout>
    </FlexBox>
  );
};

/**
 * Компонент роутера - обязательная часть ядра Geograffee
 *
 * Отсюда маршрутизация по мини-приложению
 */
const Routes: FC = () => (
  <Switch>
    <Route path="/" component={Example} />
  </Switch>
);

/**
 * Приложение. Это точка входа. Все начинается отсюда.
 * Провайдеры, оформление страницы - да все, что угодно!
 *
 * Любое свершение начинается с точки входа - удачного кодинга :)
 *
 * Переменная `mode` может принимать 3 значения: 'develop', 'staging' и 'production'
 */
const App: FC<{ mode: AppNodeMode }> = () => (
  // @ts-ignore
  <BrowserRouter>
    <Routes />
  </BrowserRouter>
);

const app = { App, manifest };

// Это обязательная часть. Она очень важна!
export default app;
import { FC, ReactNode } from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import {
  Button,
  Colors,
  FInter,
  FlexBox,
  useToasts,
} from '@brg/ui';
import { AppNodeMode, MiniApplicationManifest } from 'core/helpers/dna';
import { PageViewportLayout } from '../../components/template';
import { whiteTheme } from '../../themes/whiteTheme';
import type { ApplicationThemeT } from '../../themes';
import { ApplicationTheme } from '../../themes';
import { isLowMode } from '../../helpers/lowmode';
import { customThemeNameOrUndefined } from '../../helpers/customTheme';

const manifest: MiniApplicationManifest = {
  // Название мини-приложения - и document.title
  title: 'Console',

  // объект редукторов (redux)
  reducer: {},

  theme: whiteTheme as ApplicationThemeT,

  // авторизация
  auth: {
    productionClientId: '',
    developClientId: '',
  },
};

const ConsoleSectionComponentStyle = {
  border: `1px solid ${Colors.galBlack31}`,
  padding: '16px',
  borderRadius: '8px',
  minHeight: '180px',
};

export interface ConsoleSectionComponentProps {
  title: string;
  description: ReactNode;
  children?: ReactNode;
}
export const ConsoleSectionComponent: FC<ConsoleSectionComponentProps> = ({
  title,
  description,
  children,
}) => (
  <FlexBox
    flex="1"
    direction="column"
    style={ConsoleSectionComponentStyle}
  >
    <FInter.TextFlex>{title}</FInter.TextFlex>
    <FInter.SmallTextDiv color={Colors.galBlack47}>{description}</FInter.SmallTextDiv>
    {children}
  </FlexBox>
);

/**
 * Компонент для примера работы маршрутизатора
 */
const Example: FC = () => {
  const { info } = useToasts();
  const useLow = isLowMode();
  const customTheme = customThemeNameOrUndefined();

  return (
    <FlexBox color={Colors.geoWhite100}>
      <PageViewportLayout>
        <FInter.MainTitleSmallDiv color={Colors.galBlack100}>Консоль</FInter.MainTitleSmallDiv>

        <FInter.SmallTextDiv color={Colors.galBlack73}>
          Панель разработки и управления
        </FInter.SmallTextDiv>

        <FlexBox gx={16} offsetTop={16}>
          <ConsoleSectionComponent
            title="Тема"
            description={(
              <div>
                Выбор темы повлияет на отображение проекта. Тема выбирается из
                <b> ApplicationTheme </b>
                и устанавливается в
                <b> ThemeProvider</b>
              </div>
            )}
          >
            <FlexBox offsetTop="auto" gx={8}>
              {Object.keys(ApplicationTheme).map((value, index) => value === customTheme ? null : (
                  <Button.GreenSmall
                    onClick={() => {
                      localStorage.setItem('custom-theme', value);
                      info(`установлена тема: ${value}`);
                    }}
                    key={index}
                    color={Colors.galBlack100}
                  >
                    {value}
                  </Button.GreenSmall>
                ),
              )}
              <Button.YellowSmall
                onClick={() => {
                  localStorage.removeItem('custom-theme');
                }}
              >
                remove data
              </Button.YellowSmall>
            </FlexBox>
          </ConsoleSectionComponent>
          <ConsoleSectionComponent
            title="Низкое разрешение"
            description={<div>Картинки будут принудительно загружаться в низком разрешении</div>}
          >
            <FlexBox offsetTop="auto" gx={8} align="center">
              {!useLow && (
                <Button.GreenSmall
                  onClick={() => {
                    localStorage.setItem('low-mode', 'yes');
                  }}
                >
                  включить low image mode
                </Button.GreenSmall>
              )}
              {useLow && (
                <Button.GreenSmall
                  onClick={() => {
                    localStorage.removeItem('low-mode');
                  }}
                >
                  отключить low image mode
                </Button.GreenSmall>
              )}
            </FlexBox>
          </ConsoleSectionComponent>
        </FlexBox>
        <FlexBox gx={16} offsetTop={16}>
          <ConsoleSectionComponent
            title="Очистка хранилища"
            description={(
              <div>
                Выполняет полную очистку хранилища веб-приложения (я надеюсь, вы понимаете,
                что делает эта кнопка)
              </div>
            )}
          >
            <FlexBox offsetTop={8} gy={8} offsetBottom={8} direction={'column'}>
              <FInter.SmallTextDiv>
                Local Storage: {localStorage.length} items
              </FInter.SmallTextDiv>
              <FInter.SmallTextDiv>
                Session Storage: {sessionStorage.length} items
              </FInter.SmallTextDiv>
              <FInter.SmallTextDiv>
                Cookies: {document.cookie.length} bytes
              </FInter.SmallTextDiv>
            </FlexBox>
            <FlexBox offsetTop="auto" gx={8}>
              <Button.GreenSmall
                onClick={() => {
                  localStorage.clear();
                  document.cookie = '';
                  sessionStorage.clear();
                  info('Выполнена очистка: localStorage, sessionStorage, cookies');
                }}
              >
                Очистить хранилище веб-приложения
              </Button.GreenSmall>
            </FlexBox>
          </ConsoleSectionComponent>
        </FlexBox>
        <FlexBox offsetTop={16}>
          <Button.YellowMedium onClick={() => window.location.href = '/'} style={{ width: '100%' }}>
            Закрыть консоль
          </Button.YellowMedium>
        </FlexBox>
      </PageViewportLayout>
    </FlexBox>
  );
};

/**
 * Компонент роутера - обязательная часть ядра Geograffee
 *
 * Отсюда маршрутизация по мини-приложению
 */
const Routes: FC = () => (
  <Switch>
    <Route path="/" component={Example} />
  </Switch>
);

/**
 * Приложение. Это точка входа. Все начинается отсюда.
 * Провайдеры, оформление страницы - да все, что угодно!
 *
 * Любое свершение начинается с точки входа - удачного кодинга :)
 *
 * Переменная `mode` может принимать 3 значения: 'develop', 'staging' и 'production'
 */
const App: FC<{ mode: AppNodeMode }> = () => (
  // @ts-ignore
  <BrowserRouter>
    <Routes />
  </BrowserRouter>
);

const app = { App, manifest };

// Это обязательная часть. Она очень важна!
export default app;
